import { InputHTMLAttributes, ReactNode, forwardRef } from 'react';

import { IconButton } from '@/components';
import { CircleSpinnerIcon, IconElement } from '@/components/Icons';
import { cn } from '@/lib/cn';

import { R } from '@/caregiverSpace/resources/Texts.resx';

export type InputWithIconProps = {
    onInputChange: (value: string) => void;
    onSearch: (value: string) => void;
    isSearchPending?: boolean; // Optional prop to indicate if the search is pending
    value: string; // Make value controlled from parent
    className?: string;
    icon: IconElement;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;

export const InputWithIcon = forwardRef<HTMLInputElement, InputWithIconProps>(
    (
        {
            onInputChange,
            onSearch,
            isSearchPending = false,
            value,
            className,
            icon,
            ...rest
        }: InputWithIconProps,
        ref,
    ) => {
        return (
            <div
                className={cn(
                    'inline-flex items-center justify-start gap-1',
                    className,
                )}
            >
                <input
                    className="form-control form-control-sm px-4 py-1 text-sm placeholder:text-(--color-text-secondary) placeholder:opacity-100"
                    ref={ref}
                    type="text"
                    value={value}
                    onChange={({ currentTarget }) => {
                        onInputChange(currentTarget.value);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            onSearch(value);
                        }
                    }}
                    onBlur={() => {
                        // Trigger search on blur if the value is not empty
                        // to prevent searching when user just clicks in and out
                        if (value.trim() !== '') {
                            onSearch(value);
                        }
                    }}
                    {...rest}
                />
                <IconButton
                    onClick={() => {
                        onSearch(value);
                    }}
                    icon={
                        isSearchPending ? (
                            <CircleSpinnerIcon
                                className="animate-spin"
                                variant="action"
                            />
                        ) : (
                            icon
                        )
                    }
                    tooltip={isSearchPending ? R.Searching : R.Search}
                ></IconButton>
            </div>
        );
    },
);
